import { defineComponent as _defineComponent } from 'vue'
export interface TableContext {
  filter: string | null;
}

export interface ProviderResults {
  items: DataItem[];
}

export type DataProvider = (ctx: TableContext) => Promise<ProviderResults>;

/**
 * `TableLogs` component properties.
 */
export interface Props {
  table: TablePanels.ModuleKey;
  pk: string;
  label: string;
  columns: Columns;
  operations?: Operations;
  createAction?: string;
  deleteAction?: string;
  loading?: boolean;
  tableQueryText?: string;
  filterFields?: string[];
  specialFilters?: TP.SpecialFilter<DataItem>[];
  filterTags?: TP.FilterTag[];
  infoMessage?: string | null;
}


import { computed, reactive, watch, onMounted } from 'vue';
import { TablePanel as TP } from 'table-panel';

import { api } from '@api';
import { useStore, TablePanels } from '@store';
import { isString } from '@tools/type-guards';
import { nameFormat } from '@utils/name-format';

import FormQuerySelect, {
  type Querier,
} from '@components/Form/FormQuerySelect.vue';
import Icon from '@components/Icon.vue';
import { viewFullLog } from '@store/modules/table-panels/output-log-event';

import { faSync } from '@icons/regular/faSync';
import { faInfo } from '@icons/regular/faInfo';

import { useTable, Columns, Operations, DataItem } from './table';
import { useProgressiveLoader } from './progressive-loader';
import CellOperations from './CellOperations.vue';
import TableOverlay from './TableOverlay.vue';

const __default__ = { name: 'TableLogs' };

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
    table: null,
    pk: null,
    label: null,
    columns: null,
    operations: { default: () => [] },
    createAction: { default: '' },
    deleteAction: { default: '' },
    loading: { type: Boolean },
    tableQueryText: { default: '' },
    filterFields: { default: () => [] },
    specialFilters: { default: () => [] },
    filterTags: { default: () => [] },
    infoMessage: { default: null }
  },
  setup(__props: any) {

const props = __props as { table: TablePanels.ModuleKey, pk: string, label: string, columns: Columns, operations: Operations, createAction: string, deleteAction: string, loading?: boolean, tableQueryText: string, filterFields: string[], specialFilters: TP.SpecialFilter<DataItem>[], filterTags: TP.FilterTag[], infoMessage: string | null };



const store = useStore();

const {
  queryText,
  maximized,
  tableFilters,
  fullscreen,
  items,
  displayedItems,
  page,
  allRowsSelected,
  customCells,
  refreshItems,
  ...table
} = useTable(props);

const {
  loadingPage,
  loadProgress,
  allResultsLoaded,
  loadItems,
  onScroll,
  onAnimationEnd,
} = useProgressiveLoader({ tableKey: props.table });

const queryParams = reactive({
  userId: null as string | null,
  // startDate: null as number | null,
  // endDate: null as number | null,
});

const rowCount = computed(() => items.value.length);

const classList = computed(() => {
  const items: string[] = ['table-panel', 'progressive-table'];

  if (maximized.value) {
    items.push('fullscreen');

    if (tableFilters.value.length) {
      items.push('filter-offset');
    }
  }

  return items;
});

const fields = computed(() => {
  return table.fields.value.map((field) => {
    const value = isString(field) ? { key: field } : { ...field };

    return { ...value, sortable: false };
  });
});

const tableOverlay = computed(() => {
  let text: string;
  let transparent = false;

  if (loadingPage.value) {
    text = 'Loading';
    transparent = true;
  } else if (props.loading) {
    text = 'Loading';
  } else if (queryParams.userId === null) {
    text = 'Select a User';
    transparent = true;
  } else if (!rowCount.value) {
    text = 'No logs';
    transparent = true;
  } else {
    return null;
  }

  return { text, transparent };
});

const queryUsers: Querier = async (queryText) => {
  const res = await api.users.search({
    admin: true,
    includeRoles: true,
    includeLicenses: false,
    limit: 25,
    contains: queryText,
  });

  const resultItems = res.items.map((item) => ({
    text: nameFormat(item),
    value: item.id,
  }));

  return resultItems;
};

function loadMore() {
  void loadItems();
}

watch(queryParams, (params) => {
  store.commit(`tablePanels/${props.table}/update`, {
    tableQueryParams: params,
  });

  void loadItems(true);
});

onMounted(() => {
  store.commit(`${props.table}/CLEAR`);
});

return { __sfc: true,props, store, queryText, maximized, tableFilters, fullscreen, items, displayedItems, page, allRowsSelected, customCells, refreshItems, table, loadingPage, loadProgress, allResultsLoaded, loadItems, onScroll, onAnimationEnd, queryParams, rowCount, classList, fields, tableOverlay, queryUsers, loadMore, FormQuerySelect, Icon, viewFullLog, faSync, faInfo, CellOperations, TableOverlay }
}

})